import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Check, AlertCircle } from 'lucide-react';
import type { SpreadsheetCellProps, CellState } from './types';

// Kept for backward compatibility with existing regression contract tests.
export const CELL_DEBOUNCE_MS = 250;

/**
 * SpreadsheetCell Component
 *
 * Individual editable cell with:
 * - Currency formatting in display mode
 * - Raw number input in edit mode
 * - Keyboard navigation support
 * - Visual states (editing, saving, saved, error)
 */
export const SpreadsheetCell: React.FC<SpreadsheetCellProps> = ({
  rowId,
  periodId,
  value,
  editable,
  onSave,
  onNavigate,
  isFocused,
  autoEdit = false,
  state = 'display'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const [cellState, setCellState] = useState<CellState>(state);
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const saveInFlight = useRef(false);
  const { toast } = useToast();

  /**
   * Format number as AU currency
   */
  const formatCurrency = (amount: number | null): string => {
    if (amount === null) return '-';
    const formatted = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));

    // Negative values: red text with brackets
    if (amount < 0) {
      return `(${formatted})`;
    }

    return formatted;
  };

  /**
   * Enter edit mode and notify parent of focus so keyboard navigation state is consistent.
   */
  const handleCellClick = () => {
    if (!editable || isEditing || cellState === 'saving') return;

    // Notify parent navigation hook so focusedCell stays in sync with mouse clicks
    onNavigate('self', false);

    setIsEditing(true);
    setEditValue(value?.toString() ?? '');
    setCellState('editing');
  };

  /**
   * Save cell value
   */
  const handleSave = async () => {
    if (!isEditing || saveInFlight.current) return false;
    const numericValue = parseFloat(editValue);

    if (numericValue === value || (value === null && editValue.trim() === '')) {
      setIsEditing(false);
      setCellState('display');
      return true;
    }

    if (isNaN(numericValue)) {
      setCellState('error');
      toast({ title: 'Figure not saved', description: 'Enter a valid number. Your entry has been kept.', variant: 'destructive' });
      return false;
    }

    saveInFlight.current = true;
    setCellState('saving');

    try {
      await onSave(numericValue);

      // Show success indicator
      setIsEditing(false);
      setCellState('saved');
      setTimeout(() => setCellState(current => current === 'saved' ? 'display' : current), 2000);
      return true;
    } catch (error) {
      setCellState('error');
      toast({
        title: 'Figure not saved',
        description: `${error instanceof Error ? error.message : 'The save failed. Try again.'} Your entry has been kept.`,
        variant: 'destructive',
      });
      return false;
    } finally {
      saveInFlight.current = false;
    }
  };

  /**
   * Cancel edit.
   */
  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
    setCellState('display');
  }, []);

  /**
   * Handle keyboard events in edit mode
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        handleSave().then((saved) => {
          if (saved) onNavigate('down', true);
        });
        break;
      case 'Tab':
        event.preventDefault();
        handleSave().then((saved) => {
          if (saved) onNavigate(event.shiftKey ? 'shift-tab' : 'tab', true);
        });
        break;
      case 'Escape':
        event.preventDefault();
        handleCancel();
        break;
    }
  };

  /**
   * Focus input when entering edit mode
   */
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  /**
   * Focus cell when keyboard navigation arrives.
   * When autoEdit is true (Tab/Enter navigation), auto-enter edit mode.
   */
  useEffect(() => {
    if (isFocused && !isEditing) {
      if (autoEdit && editable) {
        // Tab/Enter navigation: auto-enter edit mode so user can type immediately
        setIsEditing(true);
        setEditValue(value?.toString() ?? '');
        setCellState('editing');
      } else if (cellRef.current) {
        cellRef.current.focus();
      }
    }
  }, [isFocused, isEditing, autoEdit, editable, value]);

  /**
   * Cell styles based on state
   */
  const getCellStyles = (): string => {
    const baseStyles = 'px-3 py-1 text-right cursor-pointer border border-gray-200 relative tabular-nums';

    if (!editable) {
      return `${baseStyles} bg-gray-100 cursor-default`;
    }

    if (cellState === 'editing') {
      return `${baseStyles} bg-blue-50 border-blue-500 ring-2 ring-blue-200`;
    }

    if (cellState === 'error') {
      return `${baseStyles} bg-red-50 border-red-500`;
    }

    if (cellState === 'saving') {
      return `${baseStyles} bg-gray-50 opacity-70`;
    }

    // Input cells have subtle blue tint
    return `${baseStyles} bg-[#f0f4ff] hover:bg-blue-50`;
  };

  /**
   * Text color for negative values
   */
  const getTextColor = (): string => {
    if (value !== null && value < 0) {
      return 'text-red-600';
    }
    return 'text-gray-900';
  };

  return (
    <div
      ref={cellRef}
      className={`${getCellStyles()} ${getTextColor()}`}
      onClick={handleCellClick}
      data-testid={`spreadsheet-cell-${rowId}-${periodId}`}
      tabIndex={editable ? 0 : -1}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {isEditing ? (
        <Input
          ref={inputRef}
          type="number"
          disabled={cellState === 'saving'}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className="h-8 px-2 text-right border-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid="spreadsheet-cell-input"
          step="0.01"
        />
      ) : (
        <div className="flex items-center justify-between">
          <span className="flex-1 text-right">{formatCurrency(value)}</span>

          {/* Saved indicator */}
          {cellState === 'saved' && (
            <Check
              className="h-4 w-4 text-green-600 ml-2"
              data-testid="cell-saved-indicator"
            />
          )}

          {/* Error indicator */}
          {cellState === 'error' && (
            <AlertCircle className="h-4 w-4 text-red-600 ml-2" />
          )}
        </div>
      )}
    </div>
  );
};
